import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { AuthContext } from '../../../../contexts/AuthContext';
import { useService } from '../../../../shared/hooks/useService';
import './styles.css';

const EMOJIS = ['💪', '🔥', '👏', '🏆', '😮', '❤️', '🎂'];

const ActivityReactions = ({ activityId, initialReactions, initialReactionUsers }) => {
    const { user } = useContext(AuthContext);
    const [reactions, setReactions] = useState(initialReactions || {});
    const [reactionUsers, setReactionUsers] = useState(initialReactionUsers || {});
    const [pendingEmoji, setPendingEmoji] = useState(null);
    const service = useService();

    const currentUsername = user?.username || user?.Username;
    const currentDisplayName = user ? `${user.firstName} ${user.lastName}` : null;

    const handleReaction = async (e, emoji) => {
        e.preventDefault();
        e.stopPropagation();
        if (!currentUsername || pendingEmoji) return;

        const prevReactions = reactions;
        const currentUsers = reactions[emoji] || [];
        const isReacted = currentUsers.includes(currentUsername);

        const updated = { ...reactions };
        if (isReacted) {
            updated[emoji] = currentUsers.filter(u => u !== currentUsername);
            if (!updated[emoji].length) delete updated[emoji];
        } else {
            updated[emoji] = [...currentUsers, currentUsername];
        }
        setReactions(updated);

        if (currentDisplayName && !reactionUsers[currentUsername]) {
            setReactionUsers(prev => ({ ...prev, [currentUsername]: currentDisplayName }));
        }

        setPendingEmoji(emoji);
        try {
            const result = await service.reactToActivity(activityId, emoji);
            setReactions(result.reactions || {});
        } catch {
            setReactions(prevReactions);
        } finally {
            setPendingEmoji(null);
        }
    };

    const activeEmojis = EMOJIS.filter(e => (reactions[e] || []).length > 0);
    const inactiveEmojis = EMOJIS.filter(e => !(reactions[e] || []).length);

    return (
        <div className="activity-reactions" onClick={e => e.preventDefault()}>
            {activeEmojis.map(emoji => {
                const users = reactions[emoji] || [];
                const isReacted = currentUsername && users.includes(currentUsername);
                // eslint-disable-next-line no-unused-vars
                const tooltipText = users.map(u => reactionUsers[u] || u).join(', ');
                return (
                    <div key={emoji} className="reaction-tooltip-anchor">
                        <button
                            className={`reaction-btn reaction-btn--active${isReacted ? ' reaction-btn--mine' : ''}`}
                            onClick={e => handleReaction(e, emoji)}
                            disabled={pendingEmoji === emoji}
                        >
                            <span className="reaction-emoji">{emoji}</span>
                            <span className="reaction-count">{users.length}</span>
                        </button>
                        <div className="reaction-tooltip">
                            {users.map(u => (
                                <div key={u} style={{ whiteSpace: 'nowrap' }}>{reactionUsers[u] || u}</div>
                            ))}
                        </div>
                    </div>
                );
            })}
            <div className="reaction-picker-wrapper">
                <button className="reaction-add-btn">
                    <span>😊</span>
                </button>
                <div className="reaction-picker">
                    {inactiveEmojis.map(emoji => (
                        <button
                            key={emoji}
                            className="reaction-picker-emoji"
                            onClick={e => handleReaction(e, emoji)}
                            disabled={pendingEmoji === emoji}
                        >
                            {emoji}
                        </button>
                    ))}
                    {activeEmojis.map(emoji => {
                        const isReacted = currentUsername && (reactions[emoji] || []).includes(currentUsername);
                        return (
                            <button
                                key={emoji}
                                className={`reaction-picker-emoji${isReacted ? ' reaction-picker-emoji--mine' : ''}`}
                                onClick={e => handleReaction(e, emoji)}
                                disabled={pendingEmoji === emoji}
                            >
                                {emoji}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

ActivityReactions.propTypes = {
    activityId: PropTypes.string.isRequired,
    initialReactions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    initialReactionUsers: PropTypes.objectOf(PropTypes.string)
};

export default ActivityReactions;
